import { useDispatch, useSelector } from 'react-redux';
import { ZodError } from 'zod';
import { ChangeEvent, useState } from 'react';
import { FileImage, Loader2, Upload, X, Youtube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SectionHeader from '@/components/FullPageCarousel/SectionHeader';
import { DropZone } from '@/components/ui/drop-zone';
import ErrorMessage from '@/components/ErrorMessage';
import {
  allowedMediaExtensions,
  mediaUploadSchema,
} from '@/redux/post/post.schema';
import {
  setProjectEditorData,
  selectProjectEditorData,
  removeFileFromUploadQueue,
  addFileToUploadQueue,
} from '@/redux/post/post.slice';
import {
  AttachmentType,
  IAttachmentCategory,
  AttachmentParentType,
} from '@/redux/attachment/attachment.d';
import { useDeleteAttachmentMutation } from '@/redux/attachment/attachment.api.slice';
import hashFile from '@/lib/xxhash';

function MediaUpload({ errors }: { errors: { [key: string]: string } }) {
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const dispatch = useDispatch();
  const projectEditorData = useSelector(selectProjectEditorData);
  const [isDeleting, setIsDeleting] = useState('');
  const [deleteAttachment] = useDeleteAttachmentMutation();

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const modelFiles = event.target.files;
    if (!modelFiles || modelFiles.length === 0) return;

    const files = Array.from(modelFiles);

    await Promise.all(
      files.map(async (file) => {
        const fileHash = await hashFile(file);

        dispatch(
          addFileToUploadQueue({
            parentId: projectEditorData._id,
            parentType: AttachmentParentType.POST,
            category: IAttachmentCategory.postMedia,
            resourceType: AttachmentType.image,
            fileName: file.name,
            fileHash: fileHash.toString(),
            fileSize: file.size,
            preview: URL.createObjectURL(file),
          })
        );
      })
    );
  };

  const handleRemoveFile = async (fileHash: string) => {
    setIsDeleting(fileHash);
    dispatch(
      removeFileFromUploadQueue({
        fileHash,
        category: IAttachmentCategory.postMedia,
      })
    );
    const existingMedia = projectEditorData.mediaFiles.find(
      (media) =>
        media.fileHash === fileHash &&
        media.category === IAttachmentCategory.postMedia
    );
    if (!existingMedia) return;
    await deleteAttachment({
      attachmentId: existingMedia._id,
    });
    dispatch(
      setProjectEditorData({
        ...projectEditorData,
        mediaFiles: projectEditorData.mediaFiles.filter(
          (media) => media.fileHash !== fileHash
        ),
      })
    );
    setIsDeleting('');
  };

  const handleAddYoutubeUrl = () => {
    if (youtubeUrl.trim()) {
      try {
        mediaUploadSchema.parse({
          youtubeAttachments: [
            ...(projectEditorData.youtubeAttachments || []),
            youtubeUrl.trim(),
          ],
        });

        dispatch(
          setProjectEditorData({
            ...projectEditorData,
            youtubeAttachments: [
              ...(projectEditorData.youtubeAttachments || []),
              youtubeUrl.trim(),
            ],
          })
        );
      } catch (error) {
        if (error instanceof ZodError) {
          setErrorMessage(error.errors[0].message);
        }
      } finally {
        setYoutubeUrl('');
      }
    }
  };

  const handleRemoveYoutubeUrl = (urlToRemove: string) => {
    dispatch(
      setProjectEditorData({
        ...projectEditorData,
        youtubeAttachments: (projectEditorData.youtubeAttachments || []).filter(
          (url) => url !== urlToRemove
        ),
      })
    );
  };

  const uploadQueueMedia = projectEditorData.fileUploadQueue.filter(
    (file) => file.category === IAttachmentCategory.postMedia
  );

  const existingAndUploadQueueMedia = [
    ...projectEditorData.mediaFiles,
    ...uploadQueueMedia,
  ];

  return (
    <div className="space-y-6 font-tertiary">
      <div>
        <SectionHeader
          title="Upload your CAD files and Additional files"
          description="Select relevant documents to complete your upload"
        />
        <DropZone onDrop={handleFileChange}>
          <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
          <div className="mt-4">
            <p className="text-sm text-muted-foreground">
              Select a file or drag and drop here
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Images, Vectors, Documents
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Make sure select your display image first
            </p>
          </div>
          <Input
            type="file"
            className="hidden"
            onChange={handleFileChange}
            accept={allowedMediaExtensions.join(',')}
            id="file-upload"
            multiple
          />
          <Button
            variant="outline"
            className="mt-4 border-gradient border-2"
            onClick={() => document.getElementById('file-upload')?.click()}
          >
            Select file
          </Button>
        </DropZone>
        <ErrorMessage error={errors.mediaFiles} />
      </div>

      <div className="space-y-4">
        <div className="flex gap-2">
          <Input
            type="url"
            placeholder="Enter YouTube URL"
            value={youtubeUrl}
            onChange={(e) => setYoutubeUrl(e.target.value)}
            className="flex-1"
          />
          <Button
            variant="outline"
            onClick={handleAddYoutubeUrl}
            className="border-gradient border-2"
          >
            Add YouTube URL
          </Button>
        </div>
        <ErrorMessage error={errors.youtubeAttachments || errorMessage} />
      </div>

      <div className="flex flex-col gap-4 max-h-56 overflow-auto">
        <div className="space-y-4">
          {projectEditorData.youtubeAttachments.length > 0 &&
            projectEditorData.youtubeAttachments.map((url) => (
              <div
                className="flex items-center gap-2 py-[10px] px-[19px]"
                key={url}
              >
                <Youtube className="text-[#FF0000]" />
                <div className="flex-1 truncate">{url}</div>
                <Button
                  variant="ghost"
                  className="rounded-full cursor-pointer"
                  onClick={() => handleRemoveYoutubeUrl(url)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          {existingAndUploadQueueMedia &&
            existingAndUploadQueueMedia.length > 0 &&
            existingAndUploadQueueMedia.map((file) => {
              return (
                <div
                  className="flex items-center gap-2 py-[10px] px-[19px]"
                  key={`${file.fileName}-${file.fileSize}`}
                >
                  <FileImage className="text-[#0F91D2]" />
                  <div className="flex-1 truncate">{file.fileName}</div>
                  <Button
                    variant="ghost"
                    className="rounded-full cursor-pointer"
                    onClick={() => handleRemoveFile(file.fileHash)}
                  >
                    {isDeleting === file.fileHash ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <X className="h-3 w-3" />
                    )}
                  </Button>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

export default MediaUpload;
